import WorkplanStateDisplay from '@/components/contexts/WorkplanContext/WorkplanStateDisplay';
import { Dialog } from '@/components/Dialog';
import { Assignment } from '@/wpt-lib/interfaces/Assignment';
import {
    UpdateWorkplanEOYRatingDto,
    UpdateWorkplanStateDto,
    Workplan,
    WorkplanState,
    WorkplanSummary,
    WorkplanVersion,
} from '@/wpt-lib/interfaces/Workplan';
import usePageTitle from '@/utils/hooks/usePageTitle';
import { isWorkplanCompleted, isWorkplanReadonly } from '@/utils/isWorkplanReadonly';
import { useGetRequest, usePutRequest } from '@/utils/useStatefulRequests';
import React, { useState } from 'react';

import useNavigateOptions from '@/components/contexts/NaviateContext/useNavigateOptions';
import { isNullish } from '@/wpt-lib/utils/isNullish';
import { GoalType } from '@/wpt-lib/interfaces/PersonalGoal';
import useLabels from '@/components/contexts/LabelsContext/useLabels';
import { FormType } from '@/wpt-lib/components/contexts/FormsContext/FormsProvider';
import { Alert, AlertDescription, AlertIcon, Button, Flex, GridItem, HStack, Text, VStack } from '@chakra-ui/react';

export function goToNextVersion() {
    const url = window.location.toString();

    const start = url.lastIndexOf('/');
    const end = url.indexOf('?', start);

    let nextVersion = url.substring(0, start + 1) + (+url.substring(start + 1, end > 0 ? end : url.length) + 1);
    if (end > 0) nextVersion += url.substring(end);

    window.location.replace(nextVersion);
}

function goalReminderWarning(
    workplanVersion: WorkplanVersion,
    goalTypes: WorkplanSummary['goalTypes'],
    titles: Record<string, string>,
): string[] {
    if (![WorkplanState.DRAFT, WorkplanState.IN_PROGRESS].includes(workplanVersion.state)) return [];

    const requiredGoals: string[] = [];

    const workloadGoals: [
        keyof Pick<WorkplanVersion, 'teachingWorkloadPortion' | 'researchWorkloadPortion' | 'serviceWorkloadPortion'>,
        GoalType,
    ][] = [
        ['teachingWorkloadPortion', GoalType.TEACHING_AND_EDUCATIONAL_FACILITATION],
        ['researchWorkloadPortion', GoalType.RESEARCH_AND_SCHOLARSHIP],
        ['serviceWorkloadPortion', GoalType.SERVICE_AND_ENGAGEMENT],
    ];

    for (const [field, goalType] of workloadGoals) {
        if (workplanVersion[field] > 0 && (goalTypes[goalType] ?? 0) < 1) {
            requiredGoals.push(titles[goalType]);
        }
    }

    if (requiredGoals.length === 0) return [];

    let requiredGoalsStr = "'" + requiredGoals.join("', '") + "'";
    if (requiredGoals.length > 1) {
        const p = requiredGoalsStr.lastIndexOf(',');
        requiredGoalsStr = requiredGoalsStr.substring(0, p + 1) + ' and ' + requiredGoalsStr.substring(p + 1);
    }

    return [
        `Please note: It looks as if you have not entered a goal for ${requiredGoalsStr} as ${
            requiredGoals.length > 1 ? 'these' : 'this'
        } form a part of your workload allocation.`,
        'We recommend you add a goal for this, before submitting your workplan for review.',
    ];
}

interface SubmitForReviewButtonTileProps {
    currentAssignment: Assignment;
    workplan: Workplan;
    workplanVersion: WorkplanVersion;
}
const SubmitForReviewButtonTile = ({
    workplan,
    workplanVersion,
    currentAssignment,
}: SubmitForReviewButtonTileProps) => {
    usePageTitle(`My ${workplan.year} Workplan`);

    const { admin } = useNavigateOptions();

    const [confirmOpen, setConfirmOpen] = useState(false);

    const {
        loading: isSubmittingWorkplan,
        error: submittingError,
        makeRequest: submitWorkplan,
    } = usePutRequest<unknown, UpdateWorkplanStateDto | UpdateWorkplanEOYRatingDto>();

    const [submitWarnings, setSubmitWarnings] = useState<string[]>([]);
    const { makeRequest, loading: fetchingSummary } = useGetRequest<WorkplanSummary>();
    const goalTypeTitles = useLabels(FormType.PERSONAL_GOAL, 'goalType');

    const submitClicked = async () => {
        setSubmitWarnings([]);

        try {
            const response = await makeRequest(`workplan/${workplan.id}/summary`, {
                params: { version: workplanVersion.version },
            });

            if (response.data.goalTypes)
                setSubmitWarnings(goalReminderWarning(workplanVersion, response.data.goalTypes ?? {}, goalTypeTitles));
            else setSubmitWarnings([]);
        } catch (e) {
            /* Handled by hook */
        }

        setConfirmOpen(true);
    };

    const submitForReview = async () => {
        try {
            await submitWorkplan(
                `workplan/${workplan.id}`,
                {
                    state: workplan.state === WorkplanState.DRAFT ? WorkplanState.SUBMITTED : WorkplanState.REVIEW,
                },
                {
                    params: {
                        assignment: currentAssignment.id,
                    },
                },
            );

            if (admin && workplan.assignment?.id !== currentAssignment.id) goToNextVersion();
            else window.location.reload();
        } catch (e) {
            // handled by hook
        }
    };

    const completedDeclaration = !isNullish(workplanVersion?.conflictOfInterestDeclaration);

    return (
        <WorkplanStateDisplay
            render={(workplan) => {
                const isSubmittable =
                    workplan && !isWorkplanReadonly(workplan, false) && (completedDeclaration || admin);
                return (
                    <>
                        <Dialog
                            dialogHeader="Submit this workplan?"
                            dialogConfirmLabel="Submit"
                            isOpen={confirmOpen}
                            isLoading={isSubmittingWorkplan}
                            errorMsg={
                                submittingError
                                    ? 'Could not submit Workplan, refresh the page to try again, or return to Dashboard.'
                                    : ''
                            }
                            colorScheme="blue"
                            dialogAction={submitForReview}
                            onClose={() => setConfirmOpen(false)}
                        >
                            {submitWarnings.length > 0 && (
                                <Alert status="warning">
                                    <AlertIcon />
                                    <AlertDescription>
                                        {submitWarnings.map((warning, i) => (
                                            <Text key={i} mb={2}>
                                                {warning}
                                            </Text>
                                        ))}
                                    </AlertDescription>
                                </Alert>
                            )}
                            <Text>
                                {admin
                                    ? 'Submitting workplan on behalf of staff will create a new version of workplan.'
                                    : 'Your supervisor will be notified about your submission.\n' +
                                      'Once you have submitted your workplan, you will not be able to edit it until your supervisor returns it you.'}
                            </Text>
                        </Dialog>
                        {!isWorkplanCompleted(workplan) ? (
                            <GridItem colSpan={2}>
                                <Flex direction="row" w={'full'} justifyContent="flex-end">
                                    <VStack bg={'white'} p={6}>
                                        <HStack w="full" justifyContent="flex-end">
                                            <Button
                                                colorScheme={'blue'}
                                                isLoading={isSubmittingWorkplan || fetchingSummary}
                                                flexShrink={0}
                                                isDisabled={!isSubmittable}
                                                onClick={submitClicked}
                                            >
                                                Submit Workplan for Review
                                            </Button>
                                        </HStack>
                                    </VStack>
                                </Flex>
                            </GridItem>
                        ) : (
                            <></>
                        )}
                    </>
                );
            }}
        />
    );
};

export default SubmitForReviewButtonTile;
